import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import dayjs from 'dayjs';
import styled from 'styled-components';

import {
  firstSetOfMarketDataQuestionnaireBooleanFormFields,
  Loading,
  secondSetOfMarketDataQuestionnaireBooleanFormFields
} from '@tradingblock/components';
import { useStateSelector } from '../../../data/global/dataSelectors';
import { AdminEntitlementsFooter } from './components/EntitlementsFooter';
import { useBlockId } from '../../../components/blocks/BlockState';
import Config from '../../../config';
import { GetPendingUserMarketDataEntitlementDetailsUrl } from '@tradingblock/api';
import { useApi } from '../../../context/Api';
import {
  EntitlementStatus,
  FirstSetOfMarketDataQuestionnaireBooleanFormFields,
  MarketDataEntitlementTypes,
  MarketDataQuestionnaireYesNoQuestionsFromRequest,
  ResponseCodes,
  SecondSetOfMarketDataQuestionnaireBooleanFormFields
} from '@tradingblock/types';
import { DetailField, DetailFields } from '../../../components/basic/DetailFields';

const StyledDiv = styled.div`
  .error-msg {
    text-align: center;
  }
`;

export const AdminEntitlementsDetail: React.FunctionComponent<{}> = () => {
  const blockId = useBlockId();
  const api = useApi();

  const [errorMsg, setErrorMsg] = useState<string>('');

  const selectedEntitlementId = useStateSelector(s =>
    s.private.entitlements.entitlementDataByBlockId &&
    s.private.entitlements.entitlementDataByBlockId[blockId] &&
    s.private.entitlements.entitlementDataByBlockId[blockId].selectedEntitlementId);

  const {
    isLoading: isLoadingPendingUserMarketDataEntitlementDetails,
    data: getPendingUserMarketDataEntitlementDetailsData,
    error: errorGettingPendingUserMarketDataEntitlementDetailsData } = useSWR(
    `${Config.tradingApi}/${GetPendingUserMarketDataEntitlementDetailsUrl}`,
    () => api.entitlements.getPendingUserMarketDataEntitlementDetails(selectedEntitlementId),
    { revalidateOnFocus: false, shouldRetryOnError: false, revalidateOnMount: true },
  );

  if (!errorMsg && (errorGettingPendingUserMarketDataEntitlementDetailsData ||
    (getPendingUserMarketDataEntitlementDetailsData &&
      (getPendingUserMarketDataEntitlementDetailsData.errors || getPendingUserMarketDataEntitlementDetailsData.responseCode !== 0)))) {
    if (getPendingUserMarketDataEntitlementDetailsData && ResponseCodes[getPendingUserMarketDataEntitlementDetailsData.responseCode]) {
      setErrorMsg(ResponseCodes[getPendingUserMarketDataEntitlementDetailsData.responseCode].description);
    } else {
      setErrorMsg('There was an issue loading the data.');
    }
  }

  const fieldList = useMemo((): DetailField[] => {
    let fieldList: DetailField[] = [];

    if (getPendingUserMarketDataEntitlementDetailsData &&
      getPendingUserMarketDataEntitlementDetailsData.responseCode === 0 &&
      getPendingUserMarketDataEntitlementDetailsData.payload) {
      const {
        accountNumber,
        approvedUserMarketDataEntitlementType,
        approvingDateTime,
        approvingUserName,
        name,
        primaryAccountIdAssociatedWithUser,
        questions,
        requestDateTime,
        requestedMarketDataEntitlementType,
        status,
        userId,
        userLevel,
        userName,
      } = getPendingUserMarketDataEntitlementDetailsData.payload;
      const isPending = status === EntitlementStatus.Pending;

      // We are deleting the nonPersonalUse key because the backend still returns it but in order to match the question mapping we only want to keep the personalUse key.
      delete questions.nonPersonalUse;

      fieldList = [
        {
          Value: () => <span className="fields-title">Entitlement Request Details</span>,
        },
        {
          Value: () => null,
        },
        {
          Value: () => (
            <>
              <span className="props-title">Account Number</span>
              <br />
              <span className="mute">{accountNumber || ''}</span>
            </>
          ),
        },
        {
          Value: () => (
            <>
              <span className="props-title">User ID</span>
              <br />
              <span className="mute">{userId}</span>
            </>
          ),
        },
        {
          Value: () => (
            <>
              <span className="props-title">Name</span>
              <br />
              <span className="mute">{name}</span>
            </>
          ),
        },
        {
          Value: () => (
            <>
              <span className="props-title">Username</span>
              <br />
              <span className="mute">{userName}</span>
            </>
          ),
        },
        {
          Value: () => (
            <>
              <span className="props-title">Primary Account Id Associated With User</span>
              <br />
              <span className="mute">{primaryAccountIdAssociatedWithUser || ''}</span>
            </>
          ),
        },
        {
          Value: () => (
            <>
              <span className="props-title">User Level</span>
              <br />
              <span className="mute">{userLevel}</span>
            </>
          ),
        },
        {
          Value: () => (
            <>
              <span className="props-title">Request Date</span>
              <br />
              <span className="mute">
                {dayjs(requestDateTime).format('MM/DD/YYYY')} - {dayjs(requestDateTime).format('h:mm A')}
              </span>
            </>
          ),
        },
        {
          Value: () => (
            <>
              <span className="props-title">Requested Entitlement</span>
              <br />
              <span className="mute">
                {requestedMarketDataEntitlementType !== MarketDataEntitlementTypes.Undefined &&
                  requestedMarketDataEntitlementType.replace(/([A-Z])/g, ' $1').trim() ||
                  ''}
              </span>
            </>
          ),
        },

        {
          Value: () => (
            <>
              <span className="props-title">Approved User Entitlement</span>
              <br />
              <span className="mute">
                {!isPending
                  ? approvedUserMarketDataEntitlementType &&
                    approvedUserMarketDataEntitlementType !== MarketDataEntitlementTypes.Undefined &&
                    approvedUserMarketDataEntitlementType.replace(/([A-Z])/g, ' $1').trim() || ''
                  : 'Pending approval'}
              </span>
            </>
          ),
        },
        {
          Value: () => (
            <>
              <span className="props-title">Approving User</span>
              <br />
              <span className="mute">{!isPending ? (approvingUserName || '') : 'Pending approval'}</span>
            </>
          ),
        },
        {
          Value: () => (
            <>
              <span className="props-title">Approval Date</span>
              <br />
              <span className="mute">
                {
                  !isPending ?
                    ((approvingDateTime &&
                    `${dayjs(approvingDateTime).format('MM/DD/YYYY')} - ${dayjs(approvingDateTime).format('h:mm A')}`) || ''):
                  'Pending approval'
                }
              </span>
            </>
          ),
        },
        {
          Value: () => null,
        },
        {
          Value: () => <span className="fields-title">Entitlement Request Questionnaire Details</span>,
        },
        {
          Value: () => null,
        },
        ...Object.keys(questions).map((questionsKey) => ({
          Value: () => (
            <>
              <span className="props-title">{firstSetOfMarketDataQuestionnaireBooleanFormFields[questionsKey as keyof FirstSetOfMarketDataQuestionnaireBooleanFormFields] || secondSetOfMarketDataQuestionnaireBooleanFormFields[questionsKey as keyof SecondSetOfMarketDataQuestionnaireBooleanFormFields]}</span>
              <br />
              <span className="mute">{questions[questionsKey as keyof MarketDataQuestionnaireYesNoQuestionsFromRequest] ? 'Yes' : 'No'}</span>
            </>
          )
        })),
      ];
    }

    return fieldList;
  }, [getPendingUserMarketDataEntitlementDetailsData]);

  return (
    <StyledDiv>
      {isLoadingPendingUserMarketDataEntitlementDetails && <Loading />}
      {errorMsg && <p className="error-msg">{errorMsg}</p>}
      {!isLoadingPendingUserMarketDataEntitlementDetails && !errorMsg &&
        <div className="screen-body-section">
          <DetailFields fieldList={fieldList} />
        </div>}
      {!isLoadingPendingUserMarketDataEntitlementDetails && <AdminEntitlementsFooter />}
    </StyledDiv>
  );
};
